/**
 * authApiService
 * Servicio para manejar los endpoints de autenticación del backend
 * VERSION: 2.0 - CON LOGS DE DEBUG
 */

import { jwtApiService } from "./jwtApiService";

console.log("🔥🔥🔥 [authApiService.js] ARCHIVO CARGADO - VERSION 2.0 🔥🔥🔥");

/**
 * Registra un nuevo usuario con email, password y wallet
 * @param {string} name - Nombre completo del usuario
 * @param {string} email - Email del usuario
 * @param {string} password - Contraseña
 * @param {string} walletAddress - Dirección de la wallet conectada
 * @returns {Promise} Respuesta del servidor
 */
export async function register(name, email, password, walletAddress) {
  try {
    const endpoint = "/auth/register";

    const payload = {
      name,
      email,
      password,
      walletAddress,
    };

    console.log("[AuthApiService] Registrando usuario:", {
      email,
      walletAddress,
      nameLength: name?.length,
      emailLength: email?.length,
      passwordLength: password?.length,
    });

    const response = await jwtApiService.post(endpoint, payload);

    console.log("[AuthApiService] Registro exitoso:", response);

    return {
      success: true,
      data: response,
      message:
        "Usuario registrado exitosamente. Por favor verifica tu email con el código OTP.",
    };
  } catch (error) {
    console.error("[AuthApiService] Error en registro:", error);
    console.error(
      "[AuthApiService] Error response data:",
      error.response?.data,
    );
    console.error(
      "[AuthApiService] Error response status:",
      error.response?.status,
    );

    // Extraer mensaje de error del backend
    let errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "Error al registrar usuario";

    // Si el mensaje es un array, convertirlo a string
    if (Array.isArray(errorMessage)) {
      errorMessage = errorMessage.join(". ");
    } else if (typeof errorMessage === "object" && errorMessage !== null) {
      // Si es un objeto, intentar extraer el mensaje
      errorMessage = errorMessage.message || JSON.stringify(errorMessage);
    }

    const err = new Error(errorMessage);
    err.success = false;
    err.originalError = error.response?.data || error;
    err.status = error.response?.status;
    throw err;
  }
}

/**
 * Primer paso de login: validar email y password
 * @param {string} email - Email del usuario
 * @param {string} password - Contraseña
 * @returns {Promise} Respuesta del servidor
 */
export async function login(email, password) {
  try {
    const endpoint = "/auth/login";

    const payload = {
      email,
      password,
    };

    console.log("[AuthApiService] Iniciando login:", { email });

    const response = await jwtApiService.post(endpoint, payload);

    console.log("[AuthApiService] Login exitoso:", response);

    return {
      success: true,
      data: response,
      message:
        "Credenciales válidas. Por favor firma el mensaje con tu wallet.",
    };
  } catch (error) {
    console.error("[AuthApiService] Error en login:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "Error al iniciar sesión";

    const err = new Error(errorMessage);
    err.success = false;
    err.originalError = error.response?.data || error;
    err.status = error.response?.status;
    throw err;
  }
}

/**
 * Solicita un mensaje de login para firmar con la wallet
 * @param {string} loginSessionId - ID de sesión obtenido del primer paso de login
 * @returns {Promise} Mensaje para firmar
 */
export async function loginNonce(loginSessionId) {
  try {
    const endpoint = "/auth/login/nonce";

    const payload = {
      loginSessionId,
    };

    console.log("[AuthApiService] Solicitando nonce para login:", {
      loginSessionId,
    });
    console.log("[AuthApiService] Payload completo:", payload);

    const response = await jwtApiService.post(endpoint, payload);

    console.log("[AuthApiService] Nonce recibido:", response);

    return {
      success: true,
      data: response,
      message: response.message || "Mensaje generado para firmar",
    };
  } catch (error) {
    console.error("[AuthApiService] Error obteniendo nonce de login:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "Error al obtener mensaje para firmar";

    const err = new Error(errorMessage);
    err.success = false;
    err.originalError = error.response?.data || error;
    err.status = error.response?.status;
    throw err;
  }
}

/**
 * Verifica la firma del mensaje y devuelve JWT
 * @param {string} loginSessionId - ID de sesión obtenido del primer paso de login
 * @param {string} signature - Firma del mensaje
 * @returns {Promise} Token JWT
 */
export async function loginVerify(loginSessionId, signature) {
  try {
    const endpoint = "/auth/login/verify";

    const payload = {
      loginSessionId,
      signature,
    };

    console.log("[AuthApiService] Verificando firma de login:", {
      loginSessionId,
    });

    const response = await jwtApiService.post(endpoint, payload);

    console.log("[AuthApiService] Verificación exitosa, token recibido");

    return {
      success: true,
      data: response,
      token: response.accessToken || response.token,
      message: "Login exitoso",
    };
  } catch (error) {
    console.error("[AuthApiService] Error verificando firma de login:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "Error al verificar firma";

    const err = new Error(errorMessage);
    err.success = false;
    err.originalError = error.response?.data || error;
    err.status = error.response?.status;
    throw err;
  }
}

/**
 * Verifica que la wallet conectada coincide con la asociada al usuario logueado
 * @param {string} walletAddress - Dirección de la wallet a verificar
 * @returns {Promise} Resultado de la verificación
 */
export async function verifyWallet(walletAddress) {
  try {
    const endpoint = "/auth/verify-wallet";

    const payload = {
      walletAddress,
    };

    console.log("[AuthApiService] Verificando wallet:", { walletAddress });

    const response = await jwtApiService.post(endpoint, payload);

    console.log("[AuthApiService] Verificación de wallet exitosa:", response);

    return {
      success: true,
      data: response,
      message: "Wallet verificada correctamente",
    };
  } catch (error) {
    console.error("[AuthApiService] Error verificando wallet:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "Error al verificar wallet";

    const err = new Error(errorMessage);
    err.success = false;
    err.originalError = error.response?.data || error;
    err.status = error.response?.status;
    throw err;
  }
}

export async function forgotPassword(email) {
  try {
    const endpoint = "/auth/forgot-password";
    const payload = { email };

    console.log("[AuthApiService] Solicitando reset de contraseña:", { email });

    const response = await jwtApiService.post(endpoint, payload);

    return {
      success: true,
      data: response,
      message:
        response.message ||
        "Si el email existe, se ha enviado un link de reset a tu correo.",
    };
  } catch (error) {
    console.error("[AuthApiService] Error en forgot password:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Error al solicitar reset de contraseña";

    const err = new Error(errorMessage);
    err.success = false;
    err.originalError = error.response?.data || error;
    throw err;
  }
}

export async function confirmPasswordReset(uid, token, newPassword, email) {
  try {
    const endpoint = "/auth/password-reset-confirm";
    const payload = {
      uid,
      token,
      newPassword,
      ...(email && { email }), // Incluir email solo si está presente
    };

    console.log("[AuthApiService] Confirmando reset de contraseña:", {
      uid,
      token,
      hasEmail: !!email,
    });

    const response = await jwtApiService.post(endpoint, payload);

    return {
      success: true,
      data: response,
      message:
        response.message ||
        "Contraseña reseteada exitosamente. Ya puedes iniciar sesión.",
    };
  } catch (error) {
    console.error(
      "[AuthApiService] Error confirmando reset de contraseña:",
      error,
    );

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Error al confirmar reset de contraseña";

    const err = new Error(errorMessage);
    err.success = false;
    err.originalError = error.response?.data || error;
    throw err;
  }
}

export async function setNewPassword(walletAddress, newPassword) {
  console.log("🚀 [AuthApiService] FUNCIÓN setNewPassword EJECUTADA");
  console.log("🚀 [AuthApiService] Parámetros recibidos:", {
    walletAddress,
    passwordLength: newPassword?.length,
    passwordDefined: !!newPassword,
  });

  try {
    const endpoint = "/auth/set-new-password";
    const payload = { walletAddress, newPassword };

    console.log("[AuthApiService] 📡 Iniciando setNewPassword");
    console.log("[AuthApiService]    Endpoint:", endpoint);
    console.log("[AuthApiService]    Wallet:", walletAddress);
    console.log("[AuthApiService]    Password length:", newPassword.length);
    console.log("[AuthApiService]    Payload:", payload);

    console.log("[AuthApiService] 🔵 Llamando a jwtApiService.post()...");
    const response = await jwtApiService.post(endpoint, payload);
    console.log("[AuthApiService] 🔵 jwtApiService.post() completado");

    console.log(
      "[AuthApiService] ✅ Respuesta recibida del backend:",
      response,
    );
    console.log("[AuthApiService]    Tipo:", typeof response);
    console.log("[AuthApiService]    Es null?", response === null);
    console.log("[AuthApiService]    Es undefined?", response === undefined);
    console.log("[AuthApiService]    Message:", response?.message);

    const resultToReturn = {
      success: true,
      data: response,
      message: response?.message || "Nueva contraseña establecida exitosamente",
    };

    console.log("[AuthApiService] 🎁 Retornando:", resultToReturn);

    return resultToReturn;
  } catch (error) {
    console.error("[AuthApiService] ❌ Error capturado en catch:", error);
    console.error("[AuthApiService]    Tipo de error:", typeof error);
    console.error("[AuthApiService]    Error.message:", error?.message);
    console.error("[AuthApiService]    Error.response:", error?.response);
    console.error(
      "[AuthApiService]    Error.response.data:",
      error?.response?.data,
    );
    console.error(
      "[AuthApiService]    Error.response.status:",
      error?.response?.status,
    );

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Error al establecer nueva contraseña";

    const err = new Error(errorMessage);
    err.success = false;
    err.originalError = error.response?.data || error;

    console.error("[AuthApiService] 🚫 Lanzando error:", err);

    throw err;
  }
}

const authApi = {
  register,
  login,
  loginNonce,
  loginVerify,
  verifyWallet,
  forgotPassword,
  setNewPassword,
  confirmPasswordReset,
};
export default authApi;
